import { collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore/lite';
import { db } from '../firebaseConfig';
import defaultJourney from '../datamodel/journey.json';
import { normalizeJourneyData, validateJourneyDataForSave } from './actionItemModel';
import {
  createLocalJourneyRecord,
  deleteLocalJourney,
  duplicateLocalJourney,
  getLocalJourney,
  importFirestoreJourneyToLocal as cacheFirestoreJourney,
  replaceLocalJourneyData,
  saveLocalJourney,
} from './localJourneyStore';

export const DEFAULT_JOURNEY_ID = 'default';

export const makeJourneyId = (value) => {
  const id = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

  return id || DEFAULT_JOURNEY_ID;
};

const createEmptyJourneyData = (goalName) => normalizeJourneyData({
  fromTheme: goalName || 'Start',
  endTheme: goalName || 'Goal',
  startState: goalName || 'Start',
  context: '',
  endState: goalName || 'Goal',
  steps: [],
});

const isPermissionDenied = (error) => (
  error?.code === 'permission-denied' || error?.code === 'firestore/permission-denied'
);

const readFirestoreJourney = async (userId, journeyId = DEFAULT_JOURNEY_ID) => {
  if (!userId) {
    return null;
  }

  const snapshot = await getDoc(doc(db, 'users', userId, 'journeys', journeyId));
  if (snapshot.exists()) {
    return {
      id: snapshot.id,
      ...snapshot.data(),
    };
  }

  try {
    const legacySnapshot = await getDoc(doc(db, 'user-generated-document', userId));
    if (legacySnapshot.exists()) {
      return {
        id: legacySnapshot.id,
        journeyId,
        ...legacySnapshot.data(),
      };
    }

    const usersCollection = collection(db, 'user-generated-document');
    const legacyQuery = query(usersCollection, where('userId', '==', userId));
    const legacyQuerySnapshot = await getDocs(legacyQuery);

    if (legacyQuerySnapshot.empty) {
      return null;
    }

    const legacyDoc = legacyQuerySnapshot.docs[0];
    return {
      id: legacyDoc.id,
      journeyId,
      ...legacyDoc.data(),
    };
  } catch (error) {
    // Current rules intentionally expose only UID-scoped journey paths. A denied
    // probe of the deprecated shared collection means there is no accessible
    // legacy record; it is not a failure to load a new user's working copy.
    if (isPermissionDenied(error)) {
      return null;
    }

    throw error;
  }
};

const parseFirestoreData = (firestoreDoc) => {
  if (!firestoreDoc) {
    return null;
  }

  if (typeof firestoreDoc.graph === 'string') {
    return normalizeJourneyData(JSON.parse(firestoreDoc.graph));
  }

  return normalizeJourneyData(firestoreDoc.graph || firestoreDoc);
};

const dataIsDifferent = (left, right) => {
  return JSON.stringify(left || {}) !== JSON.stringify(right || {});
};

const buildCloudLocalRecord = ({ userId, journeyId, firestoreDoc, data }) => createLocalJourneyRecord({
  userId,
  journeyId,
  title: firestoreDoc?.shortname || 'Cloud Journey',
  data,
  cloudDocumentId: firestoreDoc?.journeyId || journeyId,
  source: 'firestore-imported',
  lastCloudSaveAt: firestoreDoc?.updatedAt || null,
});

export const loadJourneyWorkingCopy = async (userId, journeyId = DEFAULT_JOURNEY_ID) => {
  if (!userId) {
    throw new Error('Sign in before loading cloud journeys.');
  }

  const userLocalRecord = await getLocalJourney(userId, journeyId);
  const anonymousLocalRecord = await getLocalJourney(null, journeyId);
  const localRecord = userLocalRecord || anonymousLocalRecord;
  const firestoreDoc = await readFirestoreJourney(userId, journeyId);
  const firestoreData = parseFirestoreData(firestoreDoc);

  if (localRecord) {
    const cloudDiffers = firestoreData && dataIsDifferent(localRecord.data, firestoreData);

    if (cloudDiffers) {
      return {
        record: localRecord,
        status: 'Choose local or cloud',
        warning: 'Local and cloud journeys differ. Choose which version to use before saving to cloud.',
        cloudRecord: buildCloudLocalRecord({
          userId,
          journeyId,
          firestoreDoc,
          data: firestoreData,
        }),
      };
    }

    const record = userLocalRecord || await duplicateLocalJourney({
      sourceRecord: localRecord,
      userId,
      journeyId,
      title: localRecord.title,
      source: localRecord.source || 'local',
      cloudDocumentId: firestoreDoc ? journeyId : localRecord.cloudDocumentId,
      lastCloudSaveAt: firestoreDoc?.updatedAt || localRecord.lastCloudSaveAt || null,
    });

    return {
      record,
      status: firestoreDoc ? 'Saved to cloud' : 'Saved locally',
      warning: '',
    };
  }

  if (firestoreDoc) {
    return {
      record: null,
      status: 'Cloud version available',
      warning: 'A cloud journey is available for this account. Choose Use cloud version to load it into this browser.',
      cloudRecord: buildCloudLocalRecord({
        userId,
        journeyId,
        firestoreDoc,
        data: firestoreData,
      }),
    };
  }

  const record = createLocalJourneyRecord({
    userId,
    journeyId,
    title: 'My Journey',
    data: normalizeJourneyData(defaultJourney),
    source: 'local',
  });
  await saveLocalJourney(record);

  return {
    record,
    status: 'Saved locally',
    warning: '',
  };
};

export const loadLocalGoalJourney = async ({
  userId,
  journeyId = DEFAULT_JOURNEY_ID,
  goalName = 'My Journey',
}) => {
  const existingUserRecord = await getLocalJourney(userId || null, journeyId);
  const existingAnonymousRecord = userId ? await getLocalJourney(null, journeyId) : null;
  if (existingUserRecord) {
    return {
      record: {
        ...existingUserRecord,
        data: normalizeJourneyData(existingUserRecord.data),
      },
      status: 'Saved locally',
    };
  }

  if (existingAnonymousRecord) {
    const record = await duplicateLocalJourney({
      sourceRecord: existingAnonymousRecord,
      userId,
      journeyId,
      title: existingAnonymousRecord.title || goalName,
      source: existingAnonymousRecord.source || 'local',
      cloudDocumentId: existingAnonymousRecord.cloudDocumentId || null,
      lastCloudSaveAt: existingAnonymousRecord.lastCloudSaveAt || null,
    });

    return {
      record: {
        ...record,
        data: normalizeJourneyData(record.data),
      },
      status: 'Saved locally',
    };
  }

  const record = await saveLocalJourney(createLocalJourneyRecord({
    userId: userId || null,
    journeyId,
    title: goalName || 'My Journey',
    data: createEmptyJourneyData(goalName),
    source: 'local',
  }));

  return {
    record,
    status: 'Saved locally',
  };
};

export const saveJourneyLocal = async (journeyRecord, data) => {
  if (!journeyRecord?.id) {
    throw new Error('Cannot save local journey without a local record id.');
  }

  validateJourneyDataForSave(data);

  // Local autosave is the only automatic write path. It never writes to Firestore,
  // which avoids silent cloud overwrites while the user is editing.
  return saveLocalJourney({
    ...journeyRecord,
    data: normalizeJourneyData(data),
    source: journeyRecord.source || 'local',
  });
};

export const deleteJourney = async ({ userId, journeyId }) => {
  if (!journeyId || journeyId === DEFAULT_JOURNEY_ID) {
    throw new Error('The default goal cannot be deleted.');
  }

  const localRecord = await getLocalJourney(userId || null, journeyId);
  if (!localRecord) {
    throw new Error('This goal is no longer available.');
  }

  if (userId && localRecord.cloudDocumentId) {
    await deleteDoc(doc(db, 'users', userId, 'journeys', localRecord.cloudDocumentId));
  }

  await deleteLocalJourney(userId || null, journeyId);
};

export const saveJourneyCloud = async (
  userId,
  journeyRecord,
  data,
  journeyId = DEFAULT_JOURNEY_ID,
  currentTitle,
) => {
  if (!userId) {
    throw new Error('Sign in before saving to cloud.');
  }

  validateJourneyDataForSave(data);

  const selectedJourneyId = makeJourneyId(journeyId);
  const normalizedData = normalizeJourneyData(data);
  const updatedAt = new Date().toISOString();
  const cloudTitle = typeof currentTitle === 'string' && currentTitle.trim()
    ? currentTitle.trim()
    : journeyRecord?.title || 'My Journey';
  const payload = {
    graph: JSON.stringify(normalizedData),
    shortname: cloudTitle,
    ownerId: userId,
    journeyId: selectedJourneyId,
    updatedAt,
  };

  await setDoc(doc(db, 'users', userId, 'journeys', selectedJourneyId), payload, { merge: true });

  const savedRecord = await saveLocalJourney({
    ...journeyRecord,
    id: journeyRecord?.id || `${userId}:${DEFAULT_JOURNEY_ID}`,
    title: cloudTitle,
    cloudDocumentId: selectedJourneyId,
    lastCloudSaveAt: updatedAt,
    data: normalizedData,
    source: journeyRecord?.source || 'local',
  });

  return savedRecord;
};

export const chooseLocalJourneyVersion = async ({ userId, journeyId = DEFAULT_JOURNEY_ID, localRecord }) => {
  if (!userId || !localRecord) {
    throw new Error('Cannot use local version without a signed-in user and local journey.');
  }

  if (localRecord.id === `${userId}:${journeyId}`) {
    return localRecord;
  }

  return duplicateLocalJourney({
    sourceRecord: localRecord,
    userId,
    journeyId,
    title: localRecord.title,
    source: localRecord.source || 'local',
    cloudDocumentId: null,
  });
};

export const chooseCloudJourneyVersion = async ({ userId, journeyId = DEFAULT_JOURNEY_ID, cloudRecord }) => {
  if (!userId || !cloudRecord) {
    throw new Error('Cannot use cloud version without a signed-in user and cloud journey.');
  }

  return cacheFirestoreJourney({
    userId,
    journeyId,
    firestoreDoc: {
      graph: JSON.stringify(normalizeJourneyData(cloudRecord.data)),
      shortname: cloudRecord.title,
      journeyId,
      updatedAt: cloudRecord.lastCloudSaveAt,
    },
  });
};

export const keepBothJourneyVersions = async ({ userId, journeyId = DEFAULT_JOURNEY_ID, localRecord, cloudRecord }) => {
  if (!userId || !localRecord || !cloudRecord) {
    throw new Error('Cannot keep both versions without local and cloud journeys.');
  }

  const duplicateJourneyId = `${journeyId}-cloud-${Date.now()}`;
  await duplicateLocalJourney({
    sourceRecord: cloudRecord,
    userId,
    journeyId: duplicateJourneyId,
    title: `${cloudRecord.title || 'Cloud Journey'} copy`,
    source: 'firestore-imported',
    cloudDocumentId: null,
    lastCloudSaveAt: cloudRecord.lastCloudSaveAt || null,
  });

  return chooseLocalJourneyVersion({ userId, journeyId, localRecord });
};

export const importJourneyJsonToLocal = async ({
  userId,
  journeyId = DEFAULT_JOURNEY_ID,
  data,
}) => {
  validateJourneyDataForSave(data);

  return replaceLocalJourneyData({
    userId,
    journeyId,
    data: normalizeJourneyData(data),
    title: 'Imported Journey',
    source: 'local',
  });
};

export const importJourneyBotRecordToLocal = async ({
  userId,
  journeyId,
  data,
  title,
  cloudDocumentId,
}) => {
  return replaceLocalJourneyData({
    userId,
    journeyId,
    data: normalizeJourneyData(data),
    title: title || 'JourneyBot Journey',
    cloudDocumentId,
    source: 'journeybot-api',
  });
};

export const getStorageStatus = (journeyRecord) => {
  if (!journeyRecord) {
    return 'Cloud save available';
  }

  if (journeyRecord.lastCloudSaveAt && journeyRecord.lastLocalSaveAt <= journeyRecord.lastCloudSaveAt) {
    return 'Saved to cloud';
  }

  return 'Saved locally';
};
